export type BOM = SectionedBOM

export interface ArticleQty {
  erpItemNumber: string
  quantity: number
}

export interface ConfiguredArticleQty<T = any> extends ArticleQty {
  id: string
  item: string // 'door' | 'window'
  configData: T // TODO förekommer inte på alla items av typ ItemType t.ex. 'door', uppdatera här?
}

export type ConfigurationSectionArticle<T = any> = ArticleQty | ConfiguredArticleQty<T>

export type ConfigurationSectionType = 'BUILDING_KIT' | 'ITEMS' | 'OPTIONAL'

export interface ConfigurationSection {
  type: ConfigurationSectionType
  position: number
  ownerPosition?: number
  articles: ConfigurationSectionArticle[]
}

export type SectionedBOM = ConfigurationSection[]

type PropertyValue = string | number | boolean

type ConstraintReason = 'denied-for-option' | 'requires-option'

export type ConfigurableProps = Record<string, DiscreteProp<PropertyValue>> | { configData?: any }
export type ConfigurableItem = Omit<Item, 'props'> & {
  props: ConfigurableProps
  slotOptions: SlotOption[]
}

export interface DiscreteProp<T> {
  value: T
  type: 'discrete'
  options: {
    available: boolean
    constraint?: {
      reason: ConstraintReason
      options: {
        property: string
        value: PropertyValue
      }[]
    }
    value: T
  }[]
}

export interface Metadata {
  buildingType: 'COMPLBLDG'
  buildingArea: number
  height: number
  lengthInMm: number
  lengthInModues: number
  moduleCount: number
  roofAngle: number
  roofCladding: RoofCladding
  roofType: RoofKind
  extraRoofHeight: number
  snowZone: number
  widthInMm: number
  widthInModules: number
  foundation: 'none' | 'klara'

  doors: number
  windows: number
}

export type ItemProps = Record<string, PropertyValue> | { configData?: any }

export const itemTypes = ['door', 'window', 'garage-door', 'double-door', 'sliding-door', 'facade-section'] as const
export type ItemType = (typeof itemTypes)[number]
export type ItemGroup = 'doors' | 'windows' | 'garageDoors' | 'doubleDoors' | 'slidingDoors' | 'facadeSections'

export type Item =
  | {
    item: 'assembly'
    id: string
    erpItemNumber?: string
    props: Record<string, any> // TODO
  }
  | {
    item: 'window'
    id: string
    erpItemNumber?: string
    props: {
      moduleHeight: number
      kind: string
      color: string
      colorInside: string
      spross: string
      glass: string
      handing: string
      configData: any
      erpItemNumber?: string
    }
  }
  | {
    item: 'door'
    id: string
    erpItemNumber?: string
    props: {
      handing: string
      kind: string
      color: string
      colorInside: string
      glass: string
      kickplate: string
      hinge: string
      configData: any
      erpItemNumber?: string
      moduleHeight?: number // TODO: remove?
    }
  }
  | {
    item: 'garage-door' | 'double-door' | 'sliding-door' | 'facade-section'
    id: string
    erpItemNumber?: string
    props: {
      kind: string
      moduleWidth: number
      openingWidth: number
      openingHeight: number
      color: string
      colorInside: string
      glass: string
      kickplate: string
      handing: string
      configData: any
      erpItemNumber?: string
      moduleHeight?: number // TODO: remove?
    }
  }
// | {
//   item: ItemType
//   id: string
//   erpItemNumber: string | null
//   props: ItemProps
// }

export interface SlotOption {
  item: string
  deniedProperties: Record<string, any[]>
}

export type RoofKind = 'gable-25' | 'gable-14-extended' | 'pent-8' | 'pent-8-extended'

export type RoofCladding =
  | 'none'
  | 'tar-paper-black'
  | 'tar-paper-grey'
  | 'sheet-metal-black'
  | 'sheet-metal-dark-grey'
  | 'sheet-metal-silver-metallic'
  | 'sheet-metal-terracotta'

export enum LoftFloorKind {
  None = 'none',
  Chipboard = 'chip',
}

export enum FoundationKind {
  None = 'none',
  Chipboard = 'klara',
}

export enum SnowZone {
  U10 = 1,
  U20 = 2,
  U35 = 3.5,
}

export interface ConfigResult {
  bom: BOM
  meta: Metadata

  props: {
    length: DiscreteProp<number>
    width: DiscreteProp<number>

    roofKind: DiscreteProp<RoofKind>
    roofCladding: DiscreteProp<RoofCladding>

    gutters: DiscreteProp<string>
    snowZone: DiscreteProp<SnowZone>
    loftLeft: DiscreteProp<number>
    loftRight: DiscreteProp<number>
    loftFloor: DiscreteProp<LoftFloorKind>
    foundation: DiscreteProp<FoundationKind>
    sillColor: DiscreteProp<'white' | 'black'>
    garageDoorMotors: DiscreteProp<number>
  }

  items: Record<ItemGroup, Omit<Item, 'id'>[]>

  slotOptions: SlotOption[]
}

export type DynaMakerConfigurationSavefileBlob = any

export interface ProjectData extends ConfigResult {
  config: DynaMakerConfigurationSavefileBlob
  configVersion: number
}

// Reported by DM per change, never accumulated: a payload holds only what the latest change substituted
export interface SubstitutedArticle {
  oldErpItemNumber: string | null
  // null means DM handled it without adding a new article, typically by keeping the empty module section it sat in.
  // A missing fallback is never reported here -- DM sends the error event instead.
  newErpItemNumber: string | null
  reason: 'missing' | 'outdated'
}

export interface AppError {
  event: 'error'
  data:
  | { error: any }
  | {
    message: string
    payload: any
  }
}

export interface ApplicationReady {
  event: 'application-ready'
  data: ConfigResult
}

export interface DefaultProjectReady {
  event: 'default-project-ready'
  data: ConfigResult
}

export interface LoadedProjectReady {
  event: 'loaded-project-ready'
  data: ConfigResult & { substitutedArticles: SubstitutedArticle[] }
}

export interface LoadedProjectFailed {
  event: 'loaded-project-failed'
  data?: string
}

export interface ConfigurationUpdate {
  event: 'configuration-update'
  data: ConfigResult & { substitutedArticles: SubstitutedArticle[] }
}

export interface AppearanceUpdate {
  event: 'appearance-update'
  data: Appearance
}

export interface SavefileReady {
  event: 'savefile-ready'
  data: ProjectData
}

export interface ItemSelected {
  event: 'item-selected'
  data: ConfigurableItem
}

export interface ItemSelectedAfterInternalAction {
  event: 'item-selected-internal'
  data: ConfigurableItem
}

export interface SelectionCleared {
  event: 'selection-cleared'
}

export interface Message {
  event: 'info'
  data: {
    level: 'info' | 'warning' | 'error'
    action: string
    message: string
  }
}

export interface HistoryUpdated {
  event: 'history-updated'
  data: {
    canUndo: boolean
    canRedo: boolean
  }
}

export interface Appearance {
  showAnnotations: boolean
  cameraZoom: number
}

export type Event =
  | ApplicationReady
  | ConfigurationUpdate
  | AppearanceUpdate
  | LoadedProjectFailed
  | LoadedProjectReady
  | SavefileReady
  | ItemSelected
  | ItemSelectedAfterInternalAction
  | SelectionCleared
  | AppError
  | HistoryUpdated
